import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { useGenericManager } from "spark-core-dx/hooks";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { deepCopy, normalizeDashboardQueryState } from "../visualQueryStateUtils";
import { normalizeVisualExecutionMode } from "../visualExecutionModes";

const PAGE_COLUMN_COUNT = 10;
const DEFAULT_VISUAL_WIDTH = 3;
const DEFAULT_VISUAL_HEIGHT = 3;
const MIGRATE_DASHBOARD_MODEL_WORKSPACE_OPERATION = "MigrateDashboardModelWorkspace";

const slugify = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "page";

const createClientId = (prefix = "id") =>
  globalThis?.crypto?.randomUUID?.() ?? `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const cloneQueryState = (queryState) => ({
  ...normalizeDashboardQueryState(queryState),
  RootFilterNode: queryState?.RootFilterNode ? deepCopy(queryState.RootFilterNode) : null,
});

const cloneVisual = (visual) => ({
  ...visual,
  VisualColumns: Array.isArray(visual?.VisualColumns) ? visual.VisualColumns.map((column) => ({ ...column })) : [],
  Columns: Array.isArray(visual?.Columns) ? visual.Columns.map((column) => ({ ...column })) : [],
  RenderSettings: visual?.RenderSettings ? { ...visual.RenderSettings } : visual?.RenderSettings,
  DefaultQueryState: cloneQueryState(visual?.DefaultQueryState),
});

const clonePage = (page) => ({
  ...page,
  Visuals: Array.isArray(page?.Visuals) ? page.Visuals.map(cloneVisual) : [],
});

const cloneDetails = (details = {}) => ({
  ...details,
  Pages: Array.isArray(details?.Pages) ? details.Pages.map(clonePage) : [],
});

const normalizeVisualForSaveInternal = (visual) => {
  const clonedVisual = cloneVisual(visual);
  const {
    icon: legacyIcon,
    VisualColorPaletteOptionId,
    VisualRenderOptionId,
    PaletteName,
    ...rest
  } = clonedVisual;

  return {
    ...rest,
    Icon: rest?.Icon ?? legacyIcon ?? "",
    ExecutionMode: normalizeVisualExecutionMode(
      rest?.ModelKind,
      rest?.ExecutionMode,
      rest?.DefaultQueryState?.SupportsRealTime ?? true
    ),
    DefaultQueryState: normalizeDashboardQueryState(rest?.DefaultQueryState, {
      ModelKind: Number(rest?.ModelKind ?? 0),
      ModelId: rest?.ModelId ?? "",
    }),
  };
};

const normalizeDraftDetailsForSave = (details = {}) => ({
  ...details,
  Pages: normalizePages(details?.Pages ?? []).map((page) => ({
    ...clonePage(page),
    Visuals: Array.isArray(page?.Visuals)
      ? page.Visuals.map(normalizeVisualForSaveInternal)
      : [],
  })),
});

const normalizePages = (pages = []) => {
  const copied = pages.map((page, index) => ({
    ...clonePage(page),
    SortOrder: Number.isFinite(Number(page?.SortOrder)) ? Number(page.SortOrder) : index,
    IsDeleted: !!page?.IsDeleted,
  }));

  copied.sort((a, b) => a.SortOrder - b.SortOrder);
  copied.forEach((page, index) => {
    page.SortOrder = index;
  });

  const activePages = copied.filter((page) => !page.IsDeleted);
  let defaultAssigned = false;

  copied.forEach((page) => {
    if (page.IsDeleted) {
      page.IsDefault = false;
      return;
    }

    if (!defaultAssigned && page.IsDefault) {
      defaultAssigned = true;
      page.IsDefault = true;
      return;
    }

    page.IsDefault = false;
  });

  if (!defaultAssigned && activePages.length > 0) {
    const firstActive = activePages[0];
    const match = copied.find((page) => page.Key === firstActive.Key);
    if (match) {
      match.IsDefault = true;
    }
  }

  return copied;
};

const makeUniquePageKey = (pages = [], preferredKey = "page") => {
  const existing = new Set((pages ?? []).map((page) => page?.Key).filter(Boolean));
  let nextKey = preferredKey;
  let counter = 2;

  while (existing.has(nextKey)) {
    nextKey = `${preferredKey}-${counter}`;
    counter += 1;
  }

  return nextKey;
};

const stripPageCloneSuffix = (value = "") => value.replace(/\s\((\d+)\)\s*$/, "").trim();

const makeUniquePageName = (pages = [], preferredName = "Page") => {
  const baseName = stripPageCloneSuffix(String(preferredName || "Page").trim()) || "Page";
  const existing = new Set(
    (pages ?? [])
      .map((page) => String(page?.Name ?? "").trim().toLowerCase())
      .filter(Boolean)
  );

  let counter = 1;
  let candidate = `${baseName} (${counter})`;
  while (existing.has(candidate.toLowerCase())) {
    counter += 1;
    candidate = `${baseName} (${counter})`;
  }

  return candidate;
};

const stripCloneSuffix = (value = "") => value.replace(/\s\((\d+)\)\s*$/, "").trim();

const makeUniqueVisualName = (visuals = [], preferredName = "Visual") => {
  const baseName = stripCloneSuffix(String(preferredName || "Visual").trim()) || "Visual";
  const existing = new Set(
    (visuals ?? [])
      .map((visual) => String(visual?.Name ?? "").trim().toLowerCase())
      .filter(Boolean)
  );

  let counter = 1;
  let candidate = `${baseName} (${counter})`;
  while (existing.has(candidate.toLowerCase())) {
    counter += 1;
    candidate = `${baseName} (${counter})`;
  }

  return candidate;
};

const buildClonedVisualDraftFromPage = (visual, page) => {
  const existingVisuals = Array.isArray(page?.Visuals) ? page.Visuals.map(cloneVisual) : [];
  const columnCount = Number(page?.ColumnCount) || PAGE_COLUMN_COUNT;
  const nextWidth = Number.isFinite(Number(visual?.Width)) ? Number(visual.Width) : DEFAULT_VISUAL_WIDTH;
  const nextXBase = Number.isFinite(Number(visual?.PositionX)) ? Number(visual.PositionX) : 0;
  const nextX = nextXBase + nextWidth + 1 <= columnCount ? nextXBase + 1 : nextXBase;
  const nextY = Number.isFinite(Number(visual?.PositionY)) ? Number(visual.PositionY) + 1 : 0;
  const nextName = makeUniqueVisualName(existingVisuals, visual?.Name);

  return normalizeVisualForSaveInternal({
    ...cloneVisual(visual),
    _id: undefined,
    UniqueId: undefined,
    VisualId: undefined,
    Name: nextName,
    PositionX: nextX,
    PositionY: nextY,
  });
};

const buildPage = (page = {}, pages = []) => {
  const preferredKey = slugify(page?.Key || page?.Name || `page-${pages.filter((x) => !x?.IsDeleted).length + 1}`);
  const key = makeUniquePageKey(pages, preferredKey);

  return {
    Key: key,
    Name: page?.Name?.trim() || "New Page",
    Description: page?.Description ?? "",
    Icon: page?.Icon ?? "",
    ColumnCount: Number(page?.ColumnCount) || PAGE_COLUMN_COUNT,
    SortOrder: pages.length,
    IsDefault: !pages.some((existing) => !existing?.IsDeleted && existing?.IsDefault) || !!page?.IsDefault,
    IsDeleted: false,
    IsActive: true,
    Visuals: [],
  };
};

const getActivePages = (details) => normalizePages(details?.Pages ?? []).filter((page) => !page?.IsDeleted);

const normalizeWorkspaceId = (value) => {
  if (Array.isArray(value)) {
    return normalizeWorkspaceId(value[0]);
  }

  if (value && typeof value === "object") {
    return String(value?._id ?? value?.UniqueId ?? value?.Value ?? value?.value ?? value?.Id ?? "").trim();
  }

  return String(value ?? "").trim();
};

const getItemId = (value) => normalizeWorkspaceId(value?._id ?? value?.UniqueId ?? value);

const getFirstItem = (result) => {
  if (Array.isArray(result?.Items)) {
    return result.Items[0] ?? null;
  }

  if (Array.isArray(result) && Array.isArray(result[0]?.Items)) {
    return result[0].Items[0] ?? null;
  }

  return null;
};

const getLifecycleStatus = (item) => Number(item?.Status ?? item?.status ?? 0);

const getModelMasterId = (model) =>
  normalizeWorkspaceId(model?.MasterDataModelId ?? model?._id ?? model?.UniqueId);

const resolveDashboardWorkspaceSummaryFromMap = (dashboard, workspaceSummaryMap, { preferPublished = false } = {}) => {
  const publishedWorkspaceId = normalizeWorkspaceId(dashboard?.PublishedDataModelWorkspaceId);
  const draftWorkspaceId = normalizeWorkspaceId(dashboard?.DataModelWorkspaceId);
  const targetWorkspaceId = preferPublished
    ? (publishedWorkspaceId || draftWorkspaceId)
    : (draftWorkspaceId || publishedWorkspaceId);

  return targetWorkspaceId ? workspaceSummaryMap?.[targetWorkspaceId] ?? null : null;
};

const createOperationErrorResult = (message) => ({
  Success: false,
  DisplayMessage: message,
  Message: {
    DisplayMessage: message,
    Content: message,
  },
});

export const useDashboard = (selectedDashboardId, selectedPageId = null) => {
  const [dashboardList, setDashboardList] = useState([]);
  const [workspaceSummaryMap, setWorkspaceSummaryMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const dashboardMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Dashboard);
  const dashboardService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.Dashboard, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const workspaceService = useMemo(
    () => itemServiceFactory.GetItemService(itemServiceFactory.ItemServiceEnum.DataModelWorkspace, coreState, coreContext, true),
    [coreContext, coreState, itemServiceFactory]
  );
  const dashboardMgrRef = useRef(dashboardMgr);
  const modelMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModel);
  const modelMgrRef = useRef(modelMgr);

  useEffect(() => {
    dashboardMgrRef.current = dashboardMgr;
  }, [dashboardMgr]);

  useEffect(() => {
    modelMgrRef.current = modelMgr;
  }, [modelMgr]);

  const buildWorkspaceSummary = useCallback((workspace) => {
    if (!workspace) {
      return null;
    }

    const workspaceId = normalizeWorkspaceId(workspace?._id ?? workspace?.UniqueId);
    if (!workspaceId) {
      return null;
    }

    const displayName = String(workspace?.Name ?? workspace?.Text ?? "Model Workspace").trim() || "Model Workspace";
    const version = String(workspace?.Version ?? "").trim();

    return {
      id: workspaceId,
      displayName,
      version,
      label: version ? `${displayName} v${version}` : displayName,
      status: getLifecycleStatus(workspace),
      workspace,
    };
  }, []);

  const mergeDashboardIntoList = useCallback((dashboard) => {
    if (!dashboard?._id) {
      return;
    }

    setDashboardList((prev) => {
      const existingIndex = prev.findIndex((item) => String(item?._id) === String(dashboard._id));
      if (existingIndex < 0) {
        return [...prev, dashboard];
      }

      return prev.map((item, index) => (index === existingIndex ? dashboard : item));
    });
  }, []);

  const getDashboardList = useCallback(async () => {
    const result = await dashboardMgrRef.current.Query({
      model: "x => x.IsActive == true && x.IsDeleted == false",
    });

    if (result?.Success) {
      setDashboardList(result.Items ?? []);
      setHasError(false);
    } else {
      setDashboardList([]);
      setHasError(true);
    }

    return result;
  }, []);

  const loadDashboardById = useCallback(async (dashboardId) => {
    if (!dashboardId) {
      return null;
    }

    const result = await dashboardService.single(dashboardId);
    const normalizedResult = Array.isArray(result) ? result[0] : result;
    const dashboard = normalizedResult?.Items?.[0] ?? null;

    if (dashboard?._id) {
      mergeDashboardIntoList(dashboard);
      setHasError(false);
      return dashboard;
    }

    if (normalizedResult?.Success === false) {
      setHasError(true);
    }

    return null;
  }, [dashboardService, mergeDashboardIntoList]);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      try {
        await getDashboardList();
        if (selectedDashboardId) {
          await loadDashboardById(selectedDashboardId);
        }
      } finally {
        setIsLoading(false);
      }
    };

    void initialize();
  }, [getDashboardList, loadDashboardById, selectedDashboardId]);

  const activeDashboard = useMemo(
    () => dashboardList.find((dashboard) => String(dashboard?._id) === String(selectedDashboardId)) ?? null,
    [dashboardList, selectedDashboardId]
  );

  const activeDraftWorkspaceSummary = useMemo(
    () => resolveDashboardWorkspaceSummaryFromMap(activeDashboard, workspaceSummaryMap, { preferPublished: false }),
    [activeDashboard, workspaceSummaryMap]
  );

  const activePublishedWorkspaceSummary = useMemo(
    () => resolveDashboardWorkspaceSummaryFromMap(activeDashboard, workspaceSummaryMap, { preferPublished: true }),
    [activeDashboard, workspaceSummaryMap]
  );

  const getDashboardWorkspaceSummary = useCallback(
    (dashboard, options = {}) => resolveDashboardWorkspaceSummaryFromMap(dashboard, workspaceSummaryMap, options),
    [workspaceSummaryMap]
  );

  const getDraftPages = useCallback(
    () => normalizePages(activeDashboard?.DraftDetails?.Pages ?? []),
    [activeDashboard?.DraftDetails?.Pages]
  );

  const getTargetPageKey = useCallback(
    (pageKey = null) => {
      const pages = getDraftPages();
      const explicit = pageKey ? pages.find((page) => page.Key === pageKey) : null;
      if (explicit) {
        return explicit.Key;
      }

      const selected = selectedPageId ? pages.find((page) => page.Key === selectedPageId) : null;
      if (selected) {
        return selected.Key;
      }

      return getActivePages({ Pages: pages })[0]?.Key ?? null;
    },
    [getDraftPages, selectedPageId]
  );

  const saveDashboard = useCallback(async (dashboard) => {
    const result = await dashboardService.save(dashboard);
    return result?.first?.() ?? result;
  }, [dashboardService]);

  const createWorkspaceForDashboard = useCallback(async ({ name, description }) => {
    const normalizedName = String(name ?? "").trim();
    const normalizedDescription = String(description ?? "").trim();
    const workspaceName = `${normalizedName || "Dashboard"} Model Workspace`;
    const workspaceDescription = normalizedDescription || `Model workspace for ${normalizedName || "dashboard"}.`;

    const result = await workspaceService.save({
      Name: workspaceName,
      Description: workspaceDescription,
      Version: "1.0.0",
      IsActive: true,
      IsDeleted: false,
    });

    return Array.isArray(result) ? result[0] : result;
  }, [workspaceService]);

  const updateDashboard = useCallback(async (dashboard) => saveDashboard(dashboard), [saveDashboard]);

  const saveAndSetDashboard = useCallback(async (dashboardPatch) => {
    if (!activeDashboard?._id) {
      return { Success: false };
    }

    const updatedDashboard = {
      ...activeDashboard,
      ...dashboardPatch,
      DraftDetails: dashboardPatch?.DraftDetails
        ? normalizeDraftDetailsForSave(dashboardPatch.DraftDetails)
        : normalizeDraftDetailsForSave(activeDashboard?.DraftDetails ?? {}),
      PublishedDetails:
        dashboardPatch?.PublishedDetails == null
          ? (activeDashboard?.PublishedDetails
              ? normalizeDraftDetailsForSave(activeDashboard.PublishedDetails)
              : activeDashboard?.PublishedDetails)
          : normalizeDraftDetailsForSave(dashboardPatch.PublishedDetails),
      UniqueId: activeDashboard?._id,
      _id: activeDashboard?._id,
    };

    const response = await updateDashboard(updatedDashboard);

    if (response?.Success) {
      const savedDashboard = response.Items.first();
      mergeDashboardIntoList(savedDashboard);
      return { Success: true, dashboard: savedDashboard };
    }

    return response ?? { Success: false };
  }, [activeDashboard, mergeDashboardIntoList, updateDashboard]);

  const loadWorkspaceItemById = useCallback(async (workspaceId) => {
    const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);
    if (!normalizedWorkspaceId) {
      return null;
    }

    const result = await workspaceService?.single?.(normalizedWorkspaceId);
    return getFirstItem(result);
  }, [workspaceService]);

  useEffect(() => {
    const workspaceIds = Array.from(new Set(
      (dashboardList ?? [])
        .flatMap((dashboard) => [
          normalizeWorkspaceId(dashboard?.DataModelWorkspaceId),
          normalizeWorkspaceId(dashboard?.PublishedDataModelWorkspaceId),
        ])
        .filter(Boolean)
    ));

    const missingWorkspaceIds = workspaceIds.filter(
      (workspaceId) => !Object.prototype.hasOwnProperty.call(workspaceSummaryMap, workspaceId)
    );
    if (missingWorkspaceIds.length === 0) {
      return;
    }

    let cancelled = false;

    const loadWorkspaceSummaries = async () => {
      const entries = await Promise.all(
        missingWorkspaceIds.map(async (workspaceId) => {
          try {
            const workspace = await loadWorkspaceItemById(workspaceId);
            const summary = buildWorkspaceSummary(workspace);
            return [workspaceId, summary ?? null];
          } catch (error) {
            console.error("Dashboard workspace summary load failed:", workspaceId, error);
            return [workspaceId, null];
          }
        })
      );

      if (cancelled) {
        return;
      }

      setWorkspaceSummaryMap((prev) => {
        const nextMap = { ...prev };
        entries.forEach((entry) => {
          nextMap[entry[0]] = entry[1];
        });
        return nextMap;
      });
    };

    void loadWorkspaceSummaries();

    return () => {
      cancelled = true;
    };
  }, [buildWorkspaceSummary, dashboardList, loadWorkspaceItemById, workspaceSummaryMap]);

  const loadWorkspaceModelsById = useCallback(async (workspaceId) => {
    const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);
    if (!normalizedWorkspaceId) {
      return [];
    }

    const result = await modelMgrRef.current?.Query?.({
      model: "x => x.IsActive == true && x.IsDeleted == false && x.DataModelWorkspaceId == workspaceId",
      params: { workspaceId: normalizedWorkspaceId },
    });

    return Array.isArray(result?.Items) ? result.Items : [];
  }, []);

  const remapDashboardDetailsModelIds = useCallback((details, resolveTargetModelId) => {
    const nextDetails = cloneDetails(details ?? {});

    return {
      ...nextDetails,
      Pages: Array.isArray(nextDetails?.Pages)
        ? nextDetails.Pages.map((page) => ({
            ...clonePage(page),
            Visuals: Array.isArray(page?.Visuals)
              ? page.Visuals.map((visual) => {
                  const clonedVisual = cloneVisual(visual);
                  const currentModelId = String(
                    clonedVisual?.ModelId
                    ?? clonedVisual?.DefaultQueryState?.ModelId
                    ?? ""
                  ).trim();

                  if (!currentModelId) {
                    return clonedVisual;
                  }

                  const targetModelId = String(resolveTargetModelId(currentModelId, clonedVisual, page) ?? "").trim();
                  if (!targetModelId) {
                    const visualName = String(clonedVisual?.Name ?? clonedVisual?.VisualId ?? "Untitled Visual").trim();
                    const pageName = String(page?.Name ?? page?.Key ?? "Page").trim();
                    throw new Error(
                      `Unable to map the visual '${visualName}' on '${pageName}' to the target model workspace.`
                    );
                  }

                  return {
                    ...clonedVisual,
                    ModelId: targetModelId,
                    DefaultQueryState: {
                      ...cloneQueryState(clonedVisual?.DefaultQueryState),
                      ModelId: targetModelId,
                    },
                  };
                })
              : [],
          }))
        : [],
    };
  }, []);

  const createDashboard = useCallback(async ({ name, description, icon, isTemplate, dataModelWorkspaceId }) => {
    let resolvedWorkspaceId = normalizeWorkspaceId(dataModelWorkspaceId);
    let createdWorkspaceId = "";

    if (!resolvedWorkspaceId) {
        const workspaceResponse = await createWorkspaceForDashboard({ name, description });
        if (!workspaceResponse?.Success) {
          return workspaceResponse ?? { Success: false };
      }

      const savedWorkspace = workspaceResponse?.Items?.first?.() ?? workspaceResponse?.Items?.[0] ?? null;
      resolvedWorkspaceId = String(savedWorkspace?._id ?? "").trim();
      createdWorkspaceId = resolvedWorkspaceId;

      if (!resolvedWorkspaceId) {
        return {
          Success: false,
          DisplayMessage: "Unable to create a model workspace for the new dashboard.",
        };
      }
    }

    const firstPage = buildPage({ Name: "Page 1", Key: "page-1", IsDefault: true }, []);

    const response = await saveDashboard({
      DataModelWorkspaceId: resolvedWorkspaceId,
      PublishedDataModelWorkspaceId: null,
      PublishedDetails: null,
      DraftDetails: {
        Name: name,
        Description: description,
        Icon: icon,
        Pages: [firstPage],
      },
      State: {
        IsPublished: false,
        PublishedDate: null,
        IsTemplate: !!isTemplate,
        IsPublic: false,
      },
    });

    if (response?.Success) {
      const createdDashboard = response.Items.first();
      mergeDashboardIntoList(createdDashboard);
    } else if (createdWorkspaceId) {
      try {
        await workspaceService?.delete?.(createdWorkspaceId);
      } catch (cleanupError) {
        console.error("Model workspace cleanup failed after dashboard create error:", cleanupError);
      }
    }

    return response;
  }, [createWorkspaceForDashboard, mergeDashboardIntoList, saveDashboard, workspaceService]);

  const editDashboardById = useCallback(async (dashboardId, { name, description, icon, isTemplate, dataModelWorkspaceId }) => {
    const existingDashboard = dashboardList.find((dashboard) => String(dashboard?._id) === String(dashboardId));
    if (!existingDashboard?._id) {
      return {
        Success: false,
        DisplayMessage: "Unable to find the selected dashboard.",
      };
    }

    const existingWorkspaceId = String(existingDashboard?.DataModelWorkspaceId ?? "").trim();
    const selectedWorkspaceId = normalizeWorkspaceId(dataModelWorkspaceId);
    let resolvedWorkspaceId = selectedWorkspaceId || existingWorkspaceId;
    let createdWorkspaceId = "";

    if (!resolvedWorkspaceId) {
      const workspaceResponse = await createWorkspaceForDashboard({ name, description });
      if (!workspaceResponse?.Success) {
        return workspaceResponse ?? { Success: false };
      }

      const savedWorkspace = workspaceResponse?.Items?.first?.() ?? workspaceResponse?.Items?.[0] ?? null;
      resolvedWorkspaceId = String(savedWorkspace?._id ?? "").trim();
      createdWorkspaceId = resolvedWorkspaceId;

      if (!resolvedWorkspaceId) {
        return {
          Success: false,
          DisplayMessage: "Unable to create a model workspace for the dashboard.",
        };
      }
    }

      const response = await updateDashboard({
        ...existingDashboard,
        DataModelWorkspaceId: resolvedWorkspaceId,
      DraftDetails: normalizeDraftDetailsForSave({
        ...(cloneDetails(existingDashboard?.DraftDetails ?? existingDashboard?.PublishedDetails ?? {})),
        Name: name,
        Description: description,
        Icon: icon,
      }),
      PublishedDetails: existingDashboard?.PublishedDetails
        ? normalizeDraftDetailsForSave(cloneDetails(existingDashboard.PublishedDetails))
        : existingDashboard?.PublishedDetails,
      State: {
        ...(existingDashboard?.State ?? {}),
        IsTemplate: !!isTemplate,
      },
      UniqueId: existingDashboard?._id,
      _id: existingDashboard?._id,
    });

    if (response?.Success) {
      const savedDashboard = response?.Items?.first?.() ?? response?.Items?.[0] ?? null;
      if (savedDashboard?._id) {
        setDashboardList((prev) =>
          prev.map((dashboard) => (String(dashboard?._id) === String(savedDashboard._id) ? savedDashboard : dashboard))
        );
      }
    } else if (createdWorkspaceId) {
      try {
        await workspaceService?.delete?.(createdWorkspaceId);
      } catch (cleanupError) {
        console.error("Model workspace cleanup failed after dashboard edit error:", cleanupError);
      }
    }

    return response ?? { Success: false };
  }, [createWorkspaceForDashboard, dashboardList, updateDashboard, workspaceService]);

  const deleteDashboard = async (dashboardId, deletePermanently = false) => {
    const response = await dashboardMgr.deleteItem(dashboardId, deletePermanently);

    if (response?.Success) {
      setDashboardList((prev) => prev.filter((dashboard) => dashboard._id !== dashboardId));
    }

    return response;
  };

  const updateDraftDetails = useCallback(async (updater) => {
    const currentDraft = cloneDetails(activeDashboard?.DraftDetails);
    const nextDraft = typeof updater === "function" ? updater(currentDraft) : updater;
    return saveAndSetDashboard({ DraftDetails: normalizeDraftDetailsForSave(nextDraft) });
  }, [activeDashboard?.DraftDetails, saveAndSetDashboard]);

  const updateDraftPage = useCallback(
    async (pageKey, updater) => {
      const targetPageKey = getTargetPageKey(pageKey);
      if (!targetPageKey) {
        return { Success: false };
      }

      return updateDraftDetails((draft) => {
        const nextPages = normalizePages(draft?.Pages ?? []).map((page) => {
          if (page.Key !== targetPageKey) {
            return clonePage(page);
          }

          const updatedPage = typeof updater === "function" ? updater(clonePage(page)) : updater;
          return clonePage(updatedPage);
        });

        return {
          ...draft,
          Pages: nextPages,
        };
      });
    },
    [getTargetPageKey, updateDraftDetails]
  );

  const normalizeVisualForSave = useCallback((visual) => normalizeVisualForSaveInternal(visual), []);

  const handleDashNameChange = (name) =>
    updateDraftDetails((draft) => ({
      ...draft,
      Name: name,
    }));

  const handleDashDescriptionChange = (description) =>
    updateDraftDetails((draft) => ({
      ...draft,
      Description: description,
    }));

  const handleDashIconChange = (icon) =>
    updateDraftDetails((draft) => ({
      ...draft,
      Icon: icon,
    }));

  const editPage = async (pageKey, pagePatch) => {
    let savedPage = null;

    const result = await updateDraftDetails((draft) => {
      const pages = Array.isArray(draft?.Pages) ? draft.Pages.map(clonePage) : [];

      const nextPages = pages.map((page) => {
        if (page.Key !== pageKey) {
          return page;
        }

        const updatedPage = {
          ...page,
          ...pagePatch,
          Key: slugify(pagePatch?.Key || pagePatch?.Name || page.Key),
          Name: pagePatch?.Name?.trim() || page.Name,
          Description: pagePatch?.Description ?? "",
          Icon: pagePatch?.Icon ?? "",
          ColumnCount: Number(pagePatch?.ColumnCount) || PAGE_COLUMN_COUNT,
          IsDefault: !!pagePatch?.IsDefault,
        };

        savedPage = updatedPage;
        return updatedPage;
      });

      return {
        ...draft,
        Pages: normalizePages(nextPages),
      };
    });

    if (result?.Success) {
      const pages = result.dashboard?.DraftDetails?.Pages ?? [];
      savedPage = pages.find((page) => page.Key === savedPage?.Key) ?? savedPage;
    }

    return { ...result, page: savedPage };
  };

  const createPage = async (pageInput) => {
    let createdPage = null;

    const result = await updateDraftDetails((draft) => {
      const pages = Array.isArray(draft?.Pages) ? draft.Pages.map(clonePage) : [];
      createdPage = buildPage(
        {
          ...pageInput,
          Name: pageInput?.Name?.trim() || `Page ${pages.filter((page) => !page?.IsDeleted).length + 1}`,
          Key: pageInput?.Key || pageInput?.Name,
          IsDefault: !!pageInput?.IsDefault,
        },
        pages
      );

      const nextPages = [...pages, createdPage];

      if (createdPage.IsDefault) {
        nextPages.forEach((page) => {
          if (page.Key !== createdPage.Key) {
            page.IsDefault = false;
          }
        });
      }

      return {
        ...draft,
        Pages: normalizePages(nextPages),
      };
    });

    if (result?.Success) {
      const pages = result.dashboard?.DraftDetails?.Pages ?? [];
      createdPage = pages.find((page) => page.Key === createdPage?.Key) ?? createdPage;
    }

    return { ...result, page: createdPage };
  };

  const clonePageItem = async (pageKey) => {
    let createdPage = null;

    const result = await updateDraftDetails((draft) => {
      const pages = Array.isArray(draft?.Pages) ? draft.Pages.map(clonePage) : [];
      const sourcePage = pages.find((page) => page.Key === pageKey);
      if (!sourcePage) {
        return draft;
      }

      const nextName = makeUniquePageName(pages, sourcePage?.Name || "Page");
      const nextKey = makeUniquePageKey(pages, slugify(nextName));
      createdPage = {
        ...clonePage(sourcePage),
        Key: nextKey,
        Name: nextName,
        SortOrder: pages.length,
        IsDefault: false,
        IsDeleted: false,
        IsActive: true,
        Visuals: (sourcePage?.Visuals ?? []).map((visual) => ({
          ...cloneVisual(visual),
          _id: undefined,
          UniqueId: undefined,
          VisualId: createClientId("visual"),
        })),
      };

      return {
        ...draft,
        Pages: normalizePages([...pages, createdPage]),
      };
    });

    if (result?.Success) {
      const pages = result.dashboard?.DraftDetails?.Pages ?? [];
      createdPage = pages.find((page) => page.Key === createdPage?.Key) ?? createdPage;
    }

    return { ...result, page: createdPage };
  };

  const restorePage = async (pageKey) =>
    updateDraftDetails((draft) => ({
      ...draft,
      Pages: (draft?.Pages ?? []).map((page) =>
        page.Key === pageKey
          ? { ...page, IsDeleted: false, IsActive: true }
          : page
      ),
    }));

  const softDeletePage = async (pageKey) =>
    updateDraftDetails((draft) => ({
      ...draft,
      Pages: (draft?.Pages ?? []).map((page) =>
        page.Key === pageKey
          ? { ...page, IsDeleted: true, IsDefault: false }
          : page
      ),
    }));

  const hardDeletePage = async (pageKey) =>
    updateDraftDetails((draft) => ({
      ...draft,
      Pages: (draft?.Pages ?? []).filter((page) => page.Key !== pageKey),
    }));

  const movePage = async (pageKey, direction) => {
    const draft = cloneDetails(activeDashboard?.DraftDetails);
    const pages = normalizePages(draft?.Pages ?? []);
    const index = pages.findIndex((page) => page.Key === pageKey);

    if (index < 0) {
      return { Success: false };
    }

    const targetIndex = direction === "left" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= pages.length) {
      return { Success: false };
    }

    const reordered = [...pages];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(targetIndex, 0, moved);

    return updateDraftDetails({
      ...draft,
      Pages: reordered.map((page, orderIndex) => ({ ...page, SortOrder: orderIndex })),
    });
  };

  const reorderPage = async (pageKey, targetPageKey) => {
    if (!pageKey || !targetPageKey || pageKey === targetPageKey) {
      return { Success: false };
    }

    const draft = cloneDetails(activeDashboard?.DraftDetails);
    const pages = normalizePages(draft?.Pages ?? []);
    const sourceIndex = pages.findIndex((page) => page.Key === pageKey);
    const targetIndex = pages.findIndex((page) => page.Key === targetPageKey);

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return { Success: false };
    }

    const reordered = [...pages];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    return updateDraftDetails({
      ...draft,
      Pages: reordered.map((page, orderIndex) => ({ ...page, SortOrder: orderIndex })),
    });
  };

  const publishDashboard = async (publicState) => {
    if (!activeDashboard?._id) {
      return { Success: false };
    }

    const draftWorkspaceId = normalizeWorkspaceId(activeDashboard?.DataModelWorkspaceId);
    if (!draftWorkspaceId) {
      return createOperationErrorResult("Link a model workspace before publishing this dashboard.");
    }

    try {
      const currentWorkspace = await loadWorkspaceItemById(draftWorkspaceId);
      if (!currentWorkspace?._id) {
        return createOperationErrorResult("The linked model workspace could not be found.");
      }

      if (getLifecycleStatus(currentWorkspace) !== 2) {
        return createOperationErrorResult(
          "Switch this dashboard to the current published model workspace before publishing."
        );
      }

      const [draftModels, publishedModels] = await Promise.all([
        loadWorkspaceModelsById(draftWorkspaceId),
        loadWorkspaceModelsById(draftWorkspaceId),
      ]);

      const draftModelsById = new Map(
        draftModels.map((model) => [getItemId(model), model]).filter(([modelId]) => !!modelId)
      );
      const publishedModelsByMasterId = new Map(
        publishedModels
          .filter((model) => getLifecycleStatus(model) === 2)
          .map((model) => [getModelMasterId(model), model])
          .filter(([masterModelId]) => !!masterModelId)
      );

      const publishedDetails = remapDashboardDetailsModelIds(
        activeDashboard?.DraftDetails,
        (draftModelId) => {
          const draftModel = draftModelsById.get(String(draftModelId).trim());
          if (!draftModel) {
            throw new Error(
              `The dashboard references a model that is no longer available in '${currentWorkspace?.Name ?? "this model workspace"}'.`
            );
          }

          const publishedModel = publishedModelsByMasterId.get(getModelMasterId(draftModel));
          if (!publishedModel) {
            throw new Error(
              `No published model snapshot was found for '${draftModel?.Label ?? draftModel?.Key ?? "the selected model"}'.`
            );
          }

          return getItemId(publishedModel);
        }
      );

      return saveAndSetDashboard({
        PublishedDataModelWorkspaceId: getItemId(currentWorkspace),
        PublishedDetails: publishedDetails,
        State: {
          IsPublic: publicState,
          IsPublished: true,
          PublishedDate: new Date().toISOString(),
          IsTemplate: activeDashboard?.State?.IsTemplate,
        },
      });
    } catch (error) {
      return createOperationErrorResult(
        String(error?.message ?? "Unable to map the dashboard onto the published model workspace snapshot.")
      );
    }
  };

  const revertChanges = async () => {
    const publishedDetails = cloneDetails(activeDashboard?.PublishedDetails);
    const draftWorkspaceId = normalizeWorkspaceId(activeDashboard?.DataModelWorkspaceId);
    const publishedWorkspaceId = normalizeWorkspaceId(activeDashboard?.PublishedDataModelWorkspaceId);

    if (!publishedWorkspaceId || !draftWorkspaceId) {
      return saveAndSetDashboard({
        DraftDetails: publishedDetails,
      });
    }

    try {
      const [draftModels, publishedModels] = await Promise.all([
        loadWorkspaceModelsById(draftWorkspaceId),
        loadWorkspaceModelsById(publishedWorkspaceId),
      ]);

      const draftModelsByMasterId = new Map(
        draftModels.map((model) => [getModelMasterId(model), model]).filter(([masterModelId]) => !!masterModelId)
      );
      const publishedModelsById = new Map(
        publishedModels.map((model) => [getItemId(model), model]).filter(([modelId]) => !!modelId)
      );

      const remappedDraftDetails = remapDashboardDetailsModelIds(
        publishedDetails,
        (publishedModelId) => {
          const publishedModel = publishedModelsById.get(String(publishedModelId).trim());
          if (!publishedModel) {
            throw new Error("The published dashboard references a model snapshot that is no longer available.");
          }

          const draftModel = draftModelsByMasterId.get(getModelMasterId(publishedModel));
          if (!draftModel) {
            throw new Error(
              `The current draft model workspace is missing '${publishedModel?.Label ?? publishedModel?.Key ?? "a published model"}'.`
            );
          }

          return getItemId(draftModel);
        }
      );

      return saveAndSetDashboard({
        DraftDetails: remappedDraftDetails,
      });
    } catch (error) {
      return createOperationErrorResult(
        String(error?.message ?? "Unable to restore the published dashboard into the draft workspace.")
      );
    }
  };

  const migrateDashboardModelWorkspace = useCallback(async ({
    targetPublishedDataModelWorkspaceId,
  } = {}) => {
    if (!activeDashboard?._id) {
      return createOperationErrorResult("Select a dashboard before migrating its model workspace.");
    }

    const targetWorkspaceId = normalizeWorkspaceId(targetPublishedDataModelWorkspaceId);
    if (!targetWorkspaceId) {
      return createOperationErrorResult("Select a draft or published model workspace version to migrate onto.");
    }

    try {
      const response = await dashboardMgrRef.current?.RunOperation?.({
        operationName: MIGRATE_DASHBOARD_MODEL_WORKSPACE_OPERATION,
        id: activeDashboard._id,
        isItem: true,
        queryParams: {
          targetPublishedDataModelWorkspaceId: targetWorkspaceId,
        },
      });

      if (response?.Success) {
        const savedDashboard = response?.Item ?? response?.Items?.[0] ?? null;
        if (savedDashboard?._id) {
          mergeDashboardIntoList(savedDashboard);
        }

        return {
          ...response,
          dashboard: savedDashboard,
        };
      }

      return response ?? createOperationErrorResult("Unable to migrate the dashboard model workspace.");
    } catch (error) {
      return createOperationErrorResult(
        String(error?.message ?? "Unable to migrate the dashboard model workspace.")
      );
    }
  }, [activeDashboard?._id, mergeDashboardIntoList]);

  const getPageVisuals = useCallback(
    (pageKey = null) => {
      const targetPageKey = getTargetPageKey(pageKey);
      const page = getDraftPages().find((draftPage) => draftPage.Key === targetPageKey);
      return Array.isArray(page?.Visuals)
        ? page.Visuals.filter((visual) => visual?.IsActive !== false).map(cloneVisual)
        : [];
    },
    [getDraftPages, getTargetPageKey]
  );

  const getInactivePageVisuals = useCallback(
    (pageKey = null) => {
      const targetPageKey = getTargetPageKey(pageKey);
      const page = getDraftPages().find((draftPage) => draftPage.Key === targetPageKey);
      return Array.isArray(page?.Visuals)
        ? page.Visuals.filter((visual) => visual?.IsActive === false).map(cloneVisual)
        : [];
    },
    [getDraftPages, getTargetPageKey]
  );

  const createVisual = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey) {
      return { Success: false };
    }

    const visualId = visual?.VisualId ?? createClientId("visual");
    const nextVisual = {
      ...normalizeVisualForSave(visual),
      VisualId: visualId,
      PositionX: Number.isFinite(Number(visual?.PositionX)) ? Number(visual.PositionX) : 0,
      PositionY: Number.isFinite(Number(visual?.PositionY)) ? Number(visual.PositionY) : 0,
      Width: Number.isFinite(Number(visual?.Width)) ? Number(visual.Width) : DEFAULT_VISUAL_WIDTH,
      Height: Number.isFinite(Number(visual?.Height)) ? Number(visual.Height) : DEFAULT_VISUAL_HEIGHT,
    };

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: [...(page?.Visuals ?? []).map(cloneVisual), nextVisual],
    }));
  };

  const editVisual = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !visual?.VisualId) {
      return { Success: false };
    }

    const nextVisual = normalizeVisualForSave(visual);

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: (page?.Visuals ?? []).map((existingVisual) =>
        existingVisual?.VisualId === visual.VisualId ? nextVisual : cloneVisual(existingVisual)
      ),
    }));
  };

  const deleteVisual = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !visual?.VisualId) {
      return { Success: false };
    }

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: (page?.Visuals ?? []).filter((existingVisual) => existingVisual?.VisualId !== visual.VisualId).map(cloneVisual),
    }));
  };

  const hideVisual = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !visual?.VisualId) {
      return { Success: false };
    }

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: (page?.Visuals ?? []).map((existingVisual) =>
        existingVisual?.VisualId === visual.VisualId
          ? { ...cloneVisual(existingVisual), IsActive: false }
          : cloneVisual(existingVisual)
      ),
    }));
  };

  const cloneVisualItem = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !visual?.VisualId) {
      return { Success: false };
    }

    const nextVisualId = createClientId("visual");

    return updateDraftPage(targetPageKey, (page) => {
      const existingVisuals = Array.isArray(page?.Visuals) ? page.Visuals.map(cloneVisual) : [];
      const normalizedClone = {
        ...buildClonedVisualDraftFromPage(visual, page),
        VisualId: nextVisualId,
      };

      return {
        ...page,
        Visuals: [...existingVisuals, normalizedClone],
      };
    });
  };

  const buildClonedVisualDraft = useCallback(
    (visual, pageKey = null) => {
      const targetPageKey = getTargetPageKey(pageKey);
      if (!targetPageKey || !visual) {
        return null;
      }

      const page = getDraftPages().find((draftPage) => draftPage.Key === targetPageKey);
      if (!page) {
        return null;
      }

      return buildClonedVisualDraftFromPage(visual, page);
    },
    [getDraftPages, getTargetPageKey]
  );

  const clearAllVisuals = async (pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey) {
      return { Success: false };
    }

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: [],
    }));
  };

  const activateVisual = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !visual?.VisualId) {
      return { Success: false };
    }

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: (page?.Visuals ?? []).map((existingVisual) =>
        existingVisual?.VisualId === visual.VisualId
          ? { ...cloneVisual(existingVisual), IsActive: true }
          : cloneVisual(existingVisual)
      ),
    }));
  };

  const toggleVisualLock = async (visual, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !visual?.VisualId) {
      return { Success: false };
    }

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: (page?.Visuals ?? []).map((existingVisual) =>
        existingVisual?.VisualId === visual.VisualId
          ? { ...cloneVisual(existingVisual), IsLocked: !existingVisual?.IsLocked }
          : cloneVisual(existingVisual)
      ),
    }));
  };

  const editVisualCoordinates = async (gridItem, pageKey = null) => {
    const targetPageKey = getTargetPageKey(pageKey);
    if (!targetPageKey || !gridItem?.i) {
      return { Success: false };
    }

    return updateDraftPage(targetPageKey, (page) => ({
      ...page,
      Visuals: (page?.Visuals ?? []).map((existingVisual) =>
        String(existingVisual?.VisualId) === String(gridItem.i)
          ? {
              ...cloneVisual(existingVisual),
              PositionX: Number(gridItem.x),
              PositionY: Number(gridItem.y),
              Width: Number(gridItem.w),
              Height: Number(gridItem.h),
            }
          : cloneVisual(existingVisual)
      ),
    }));
  };

  return {
    dashboardList,
    workspaceSummaryMap,
    getDashboardWorkspaceSummary,
    activeDraftWorkspaceSummary,
    activePublishedWorkspaceSummary,
    activeDashboard,
    createDashboard,
    editDashboardById,
    deleteDashboard,
    handleDashNameChange,
    handleDashDescriptionChange,
    handleDashIconChange,
    createPage,
    clonePage: clonePageItem,
    editPage,
    restorePage,
    softDeletePage,
    hardDeletePage,
    movePage,
    reorderPage,
    publishDashboard,
    migrateDashboardModelWorkspace,
    revertChanges,
    getPageVisuals,
    getInactivePageVisuals,
    clearAllVisuals,
    createVisual,
    editVisual,
    deleteVisual,
    hideVisual,
    activateVisual,
    buildClonedVisualDraft,
    cloneVisual: cloneVisualItem,
    toggleVisualLock,
    editVisualCoordinates,
    isLoading,
    hasError,
  };
};
